/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends from
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import {
  byKeys,
  filterBy,
  findBy,
  mapBy,
  sortBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
// Ember Imports
import { isArray } from '@ember/array';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import moment from 'moment';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import { saveWithMedia } from '@linc-technologies/linced-common/utils/post-media';
import { rollbackRecord } from '@linc-technologies/linced-common/utils/record-rollback';

const pronouns = {
  M: [
    { token: 'he', pattern: /\b(he)\b/g },
    { token: 'his', pattern: /\b(his)\b/g },
    { token: 'him', pattern: /\b(him)\b/g },
    { token: 'He', pattern: /\b(He)\b/g },
    { token: 'His', pattern: /\b(His)\b/g },
    { token: 'Him', pattern: /\b(Him)\b/g },
    { token: 'himself', pattern: /\b(himself)\b/g },
    { token: 'Himself', pattern: /\b(Himself)\b/g },
  ],
  F: [
    { token: 'she', pattern: /\b(she)\b/g },
    { token: 'hers', pattern: /\b(hers)\b/g },
    { token: 'her', pattern: /\b(her)\b/g },
    { token: 'She', pattern: /\b(She)\b/g },
    { token: 'Hers', pattern: /\b(Hers)\b/g },
    { token: 'Her', pattern: /\b(Her)\b/g },
    { token: 'herself', pattern: /\b(herself)\b/g },
    { token: 'Herself', pattern: /\b(Herself)\b/g },
  ],
  N: [
    { token: 'they', pattern: /\b(they)\b/g },
    { token: 'their', pattern: /\b(their)\b/g },
    { token: 'them', pattern: /\b(them)\b/g },
    { token: 'They', pattern: /\b(They)\b/g },
    { token: 'Their', pattern: /\b(Their)\b/g },
    { token: 'Them', pattern: /\b(Them)\b/g },
    { token: 'themself', pattern: /\b(themself)\b/g },
    { token: 'Themself', pattern: /\b(Themself)\b/g },
  ],
};

// copyMedia makes an unsaved copy of a post's media record for another post.
// The copy is owned by `person`, so staff reusing someone else's post can
// still access it.
function copyMedia(store, record, school, person) {
  return store.createRecord('media', {
    ...record.getProperties(
      'createTime',
      'updateTime',
      'sharedWith',
      'mediaType',
      'mediaSource',
      'mimeType',
      'fileName',
      'storageKey',
      'icon',
      'fileSize',
      'altText',
      'thumbnailUrl',
    ),
    schoolId: school,
    personId: person,
    image: record.image?.copy() ?? null,
    video: record.video?.copy() ?? null,
    renditions: record.renditions?.map((rendition) => rendition.copy()) ?? [],
  });
}

export default class PostReuser extends Component {
  // Services
  @service
  options;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  students;

  @service
  toaster;

  editorOptions = Object.freeze(defaultTinyMCEConfig());

  @tracked
  selectedGroup;

  @action
  setSelectedGroup(value) {
    this.selectedGroup = value;
  }

  @tracked
  selectedLearners = [];

  @computed('students.all.[]')
  get allLearners() {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.get('students.all');
    return this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.LEARNER);
  }

  @computed('selectedLearners.[]', 'allLearners.[]')
  get remainingLearners() {
    let originalLearnerId = this.post.get('person.id');

    return this.allLearners.filter((source) => {
      let isSelected = this.selectedLearners.some(function (learner) {
        return source.id === learner.id;
      });
      return !isSelected && source.id !== originalLearnerId;
    });
  }

  @optionAlias('core:groups:groupings')
  groupings;

  @tracked
  filteredGroups = [];

  @computed('filteredGroups', 'strings.homeroomLabel', 'groupings.[]')
  get groupedGroups() {
    let groupedGroups = [];

    // Homerooms should be listed first.
    let homeroomGroups = {
      groupName: this.strings.homeroomLabel,
      options: [],
    };
    groupedGroups.push(homeroomGroups);

    // Then, any groupings (in school-defined order).
    if (isArray(this.groupings)) {
      groupedGroups = this.groupings.reduce((groups, grouping) => {
        groups.push({
          groupName: grouping.label,
          options: [],
        });
        return groups;
      }, groupedGroups);
    }

    // Then, any other (My/Shared) groups.
    let otherGroups = {
      groupName: 'Other',
      options: [],
    };
    groupedGroups.push(otherGroups);

    // Then, auto groups (by type).
    let autoGroupTypes = uniq(
      mapBy(
        filterBy(this.filteredGroups, 'type', GroupType.AUTO).sort(
          byKeys('grouping'),
        ),
        'grouping',
      ),
    );
    let autoGroups = autoGroupTypes.map((type) => {
      return {
        groupName: type,
        options: [],
      };
    });
    groupedGroups.push(...autoGroups);

    // Allocate all groups to their groupings/headings.
    this.filteredGroups.forEach((group) => {
      let grouped;
      switch (group.type) {
        case GroupType.HOMEROOM:
          grouped = homeroomGroups;
          break;

        case GroupType.AUTO:
          grouped = findBy(autoGroups, 'groupName', group.grouping);
          break;

        default:
          grouped = findBy(groupedGroups, 'groupName', group.grouping);
          if (isEmpty(grouped)) {
            grouped = otherGroups;
          }
          break;
      }

      if (!isEmpty(grouped)) {
        grouped.options.push(group);
      }
    });

    // Sort groups within each heading.
    groupedGroups.forEach((grouping) => {
      // Sort by type first, so My Groups come before Shared Groups.
      // This doesn't affect Homerooms or Auto Groups.
      grouping.options = sortBy(grouping.options, 'type', 'name');
    });

    // Remove any empty headings.
    groupedGroups = filterBy(groupedGroups, 'options.length');

    return groupedGroups;
  }

  @tracked
  replaceName = true;

  @action
  setReplaceName(value) {
    this.replaceName = value;
  }

  @tracked
  replacePronouns = true;

  @action
  setReplacePronouns(value) {
    this.replacePronouns = value;
  }

  @tracked
  postContent;

  @action
  setPostContent(value) {
    this.postContent = value;
  }

  @tracked
  saveInProgress;

  @computed('post.person.gender')
  get genderPronouns() {
    let gender = this.post.get('person.gender');
    if (isEmpty(gender)) {
      gender = 'N';
    }
    return pronouns[gender];
  }

  @computed('post.person.firstName')
  get nameRegExp() {
    return new RegExp(`\\b(${this.post.get('person.firstName')})\\b`, 'g');
  }

  @computed('postContent', 'replaceName', 'replacePronouns')
  get preview() {
    let wrappedToken = '<span class="reuse-post-token">$1</span>';

    // Strip existing tokens
    let content = this.postContent.replace(
      /<span class="reuse-post-token">([^<]+?)<\/span>/g,
      '$1',
    );

    // Tag name (if selected for replacement)
    if (this.replaceName) {
      content = content.replace(this.nameRegExp, wrappedToken);
    }

    // Tag pronouns (if selected for replacement)
    if (this.replacePronouns) {
      this.genderPronouns.forEach((pronoun) => {
        content = content.replace(pronoun.pattern, wrappedToken);
      });
    }
    return content;
  }

  contentForLearner(learner) {
    let content = this.postContent;

    // Replace name if enabled
    if (this.replaceName) {
      content = content.replace(this.nameRegExp, learner.firstName);
    }

    // Replace pronouns if enabled and required
    if (
      this.replacePronouns &&
      this.post.get('person.preferredGender') !== learner.get('preferredGender')
    ) {
      let gender = learner.get('preferredGender');
      if (isEmpty(gender)) {
        gender = 'N';
      }
      let newPronouns = pronouns[gender];
      this.genderPronouns.forEach((pronoun, index) => {
        content = content.replace(pronoun.pattern, newPronouns[index].token);
      });
    }

    return content;
  }

  init() {
    super.init(...arguments);
    this.selectedLearners = [];
    this.postContent = this.post.content;
    let year = moment().format('YYYY');
    this.store.query('group', { year }).then((groups) => {
      let filteredGroups = groups
        .filter((group) => {
          if (group.type === GroupType.GROUP) {
            // Omit My Groups which the current user isn't a current staff member in.
            let staff = group.get('currentStaff') ?? [];
            return !!findBy(staff, 'personId', this.session.personId);
          }
          // Include all other groups.
          return true;
        })
        .sort(
          (a, b) =>
            (a.grouping || '').localeCompare(b.grouping || '') ||
            (a.name || '').localeCompare(b.name || ''),
        );
      this.filteredGroups = filteredGroups;
    });
  }

  // reuse copies the post to each selected learner, one at a time, then
  // reports how it went.
  @action
  async reuse() {
    this.saveInProgress = true;

    // Load what every copy shares once, rather than per learner.
    let fields, media;
    try {
      ({ fields, media } = await this.loadReuseData());
    } catch {
      this.saveInProgress = false;
      this.toaster.error('Error loading the post to copy. Please try again.');
      return;
    }

    // Copy the post to each learner. A failure for one learner doesn't stop
    // the rest; they're collected so the user can re-run just those.
    let failed = [];
    let attachmentErrors = 0;
    for (let learner of this.selectedLearners) {
      try {
        if (await this.savePostFor(learner, fields, media)) {
          attachmentErrors++;
        }
      } catch {
        failed.push(learner);
      }
    }

    this.saveInProgress = false;
    this.reportReuse(failed, attachmentErrors);
  }

  // loadReuseData reads the source post's shared fields and media. The
  // relationships are awaited because createRecord takes records, not the
  // proxies a relationship returns.
  async loadReuseData() {
    let source = this.post;
    let [school, page, assessment, groups, tags, goals, modules, media] =
      await Promise.all([
        source.schoolId,
        source.pageId,
        source.assessment,
        source.groupIds,
        source.tagIds,
        source.goalIds,
        source.assessmentModuleIds,
        source.media,
      ]);

    // Fields every copy gets. Each copy is a draft authored by the current user.
    let fields = {
      ...source.getProperties(
        'postType',
        'campusId',
        'createTime',
        'updateTime',
        'displayDate',
        'subject',
      ),
      schoolId: school,
      pageId: page,
      assessment,
      groupIds: groups.slice(),
      tagIds: tags.slice(),
      goalIds: goals.slice(),
      assessmentModuleIds: modules.slice(),
      customTags: source.customTags?.toArray() ?? [],
      nceaStandardIds: source.nceaStandardIds?.toArray() ?? [],
      authorId: this.session.person,
      status: PostStatus.DRAFT,
    };

    // Each media record's school, for the same reason as above.
    let schools = await Promise.all(media.map((record) => record.schoolId));

    return {
      fields,
      media: media.map((record, i) => ({ record, school: schools[i] })),
    };
  }

  // savePostFor saves one learner's copy of the post with its own copies of
  // the media. Returns whether an attachment failed to copy. Throws if the
  // post itself didn't save.
  async savePostFor(learner, fields, media) {
    // The post, with this learner's name and pronouns in the content.
    let post = this.store.createRecord('posts/post', {
      ...fields,
      personId: learner,
      content: this.contentForLearner(learner),
    });

    // A media record belongs to a single post, so each copy needs its own.
    let copies = media.map(({ record, school }) =>
      copyMedia(this.store, record, school, this.session.person),
    );

    // Save the post, then its media, then the post again so it lists them.
    let attachmentFailed = false;
    try {
      await saveWithMedia(post, copies, () => (attachmentFailed = true));
    } catch (err) {
      // Discard the unsaved post, so it doesn't linger in the store.
      if (post.isNew) {
        rollbackRecord(post);
      }
      throw err;
    }
    return attachmentFailed;
  }

  // reportReuse tells the user how the copy went. If any learner failed, the
  // dialog stays open with just those learners selected, ready to re-run.
  reportReuse(failed, attachmentErrors) {
    // The posts were created either way, so this is reported on its own.
    if (attachmentErrors) {
      this.toaster.error(
        'Some attachments failed to copy. Edit those posts to add them again.',
      );
    }

    if (!failed.length) {
      let count = this.selectedLearners.length;
      this.onClose();
      this.toaster.success(
        `Post successfully copied for ${count} ${count === 1 ? 'student' : 'students'}!`,
      );
      return;
    }

    let label = failed.length === 1 ? 'student' : 'students';
    this.selectedLearners = failed;
    this.toaster.error(
      `Error reusing post for ${failed.length} ${label}. Please re-run this process for the remaining ${label} as now listed`,
    );
  }

  @action
  addLearner(learner) {
    this.selectedLearners = [...this.selectedLearners, learner];
  }

  @action
  removeLearner(learner) {
    this.selectedLearners = this.selectedLearners.filter((l) => l !== learner);
  }

  @action
  handleUpdateContent(content) {
    this.updateContent(content);
  }

  @action
  toggleGroup(group, checked) {
    let learners = mapBy(group.currentLearners, 'person');
    if (checked) {
      this.selectedLearners = [...this.selectedLearners, ...learners];
    } else {
      this.selectedLearners = this.selectedLearners.filter(
        (learner) => !learners.includes(learner),
      );
    }
  }

  @action
  toggleGroupLearner(learner, checked) {
    if (checked) {
      this.selectedLearners = [...this.selectedLearners, learner];
    } else {
      this.selectedLearners = this.selectedLearners.filter(
        (l) => l !== learner,
      );
    }
  }

  @action
  isGroupSelected(group) {
    return mapBy(group.currentLearners, 'person').every((person) => {
      return this.selectedLearners.includes(person);
    });
  }
}
